import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, GraduationCap } from "lucide-react";
import { api, resolveFileUrl } from "@/lib/api";
import { PageMasthead } from "@/components/ui-custom/PageMasthead";
import { RevealOnScroll } from "@/components/ui-custom/RevealOnScroll";

function BioBlock({ bio }) {
  const lines = (bio || "").split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return null;
  return (
    <div className="space-y-2 text-sm text-piks-muted leading-relaxed">
      {lines.map((line, i) =>
        line.startsWith("•") ? (
          <div key={i} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-piks-gold shrink-0" />
            <span>{line.replace(/^•\s*/, "")}</span>
          </div>
        ) : (
          <p key={i}>{line}</p>
        )
      )}
    </div>
  );
}

function TrainerCard({ t, index }) {
  const photo = t.photo_file_id ? resolveFileUrl(`/api/files/${t.photo_file_id}`) : null;
  const reversed = index % 2 === 1;
  return (
    <RevealOnScroll className={`grid sm:grid-cols-5 gap-6 sm:gap-10 items-start ${reversed ? "sm:[&>*:first-child]:order-2" : ""}`}>
      <div className="sm:col-span-2">
        <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-piks-ivory border border-piks-border">
          {photo ? (
            <img src={photo} alt={t.display_name} className="w-full h-full object-cover" data-testid={`jurulatih-photo-${t.id}`} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="font-display text-4xl text-piks-black/30">{t.display_name?.[0]}</span>
            </div>
          )}
        </div>
      </div>
      <div className="sm:col-span-3">
        <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-piks-red mb-2 uppercase" data-testid={`jurulatih-position-${t.id}`}>
          {t.position}
        </p>
        <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-piks-black mb-1" data-testid={`jurulatih-name-${t.id}`}>
          {t.display_name}
        </h3>
        {t.rank_title && (
          <p className="text-xs font-semibold text-piks-muted tracking-wide mb-4 flex items-center gap-1.5" data-testid={`jurulatih-rank-${t.id}`}>
            <ShieldCheck className="w-3.5 h-3.5 text-piks-gold shrink-0" /> {t.rank_title}
          </p>
        )}
        <BioBlock bio={t.bio} />
      </div>
    </RevealOnScroll>
  );
}

export default function Jurulatih() {
  const [trainers, setTrainers] = useState(null);

  useEffect(() => {
    api.get("/public/trainers").then((r) => setTrainers(r.data)).catch(() => setTrainers([]));
  }, []);

  return (
    <div data-testid="jurulatih-page">
      <PageMasthead
        breadcrumb="JURULATIH"
        title="Barisan Jurulatih"
        subtitle="Dilatih oleh bekas anggota polis dan tentera berpengalaman, membina kompetensi taktikal dan disiplin profesional pengawal peribadi Sarawak."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        {trainers === null ? (
          <div className="text-center py-16 text-piks-muted">Memuatkan...</div>
        ) : trainers.length === 0 ? (
          <div className="text-center py-16 text-piks-muted" data-testid="jurulatih-empty-state">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Maklumat jurulatih belum tersedia.
          </div>
        ) : (
          <div className="space-y-16 sm:space-y-24">
            {trainers.map((t, i) => (
              <div key={t.id}>
                <TrainerCard t={t} index={i} />
                {i < trainers.length - 1 && <div className="gold-divider mt-16 sm:mt-24 opacity-30" />}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
